// src/platform/OrgApiKeysPanel.jsx
import { useEffect, useState } from "react";
import { Plus, Copy, Check, AlertTriangle, Trash2, Key } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scopeBadge(scope) {
    const styles = {
        wfs: "bg-teal-500/10 border-teal-500/30 text-teal-400",
        wms: "bg-blue-500/10 border-blue-500/30 text-blue-400",
    };
    return (
        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium font-mono uppercase tracking-wide ${styles[scope] ?? "bg-gray-500/10 border-gray-500/30 text-gray-400"}`}>
            {scope}
        </span>
    );
}

function timeAgo(dateStr) {
    if (!dateStr) return "—";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Generate Key Modal
// ---------------------------------------------------------------------------

function GenerateKeyModal({ orgId, onCreated, onClose }) {
    const [name, setName]         = useState("");
    const [scope, setScope]       = useState("wfs");
    const [note, setNote]         = useState("");
    const [saving, setSaving]     = useState(false);
    const [newKey, setNewKey]     = useState(null);
    const [copied, setCopied]     = useState(false);
    const [error, setError]       = useState(null);

    async function generate() {
        if (!name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/orgs/${orgId}/api-keys`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: name.trim(), scope, note: note.trim() || null }),
                }
            );
            if (!res.ok) {
                const body = await res.json().catch((e) => { console.error("[ui/src/platform/OrgApiKeysPanel.jsx] res.json() failed (swallowed):", e); return {}; });
                throw new Error(body.error ?? `HTTP ${res.status}`);
            }
            const data = await res.json();
            setNewKey(data.key);
            onCreated();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    }

    function copyKey() {
        navigator.clipboard.writeText(newKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
            <div className="bg-bg-soft border border-bg-border rounded-lg w-full max-w-md mx-4 shadow-xl">

                <div className="px-5 py-4 border-b border-bg-border">
                    <h3 className="text-sm font-semibold text-white">Generate API Key</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        The key will only be shown once — save it immediately.
                    </p>
                </div>

                <div className="px-5 py-4 space-y-4">

                    {!newKey ? (
                        <>
                            {/* Name */}
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="e.g. QGIS - John Smith"
                                    className="w-full bg-bg border border-bg-border rounded px-2.5 py-1.5
                                               text-sm text-white placeholder:text-gray-600
                                               focus:outline-none focus:border-accent-info/50"
                                />
                            </div>

                            {/* Scope */}
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Scope <span className="text-red-400">*</span>
                                </label>
                                <div className="flex gap-2">
                                    {["wfs", "wms"].map(sc => (
                                        <button
                                            key={sc}
                                            onClick={() => setScope(sc)}
                                            className={`flex-1 py-2 rounded border text-xs font-medium uppercase tracking-wide transition-colors
                                                ${scope === sc
                                                    ? sc === "wfs"
                                                        ? "bg-teal-500/15 border-teal-500/40 text-teal-300"
                                                        : "bg-blue-500/15 border-blue-500/40 text-blue-300"
                                                    : "bg-bg border-bg-border text-gray-400 hover:text-gray-200"
                                                }`}
                                        >
                                            {sc}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-gray-500 mt-1.5">
                                    {scope === "wfs"
                                        ? "WFS key — OGC API Features / QGIS / ArcGIS WFS only"
                                        : "WMS key — WMS / WMTS / MapProxy only"}
                                </p>
                            </div>

                            {/* Note (optional) */}
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Note <span className="text-gray-600 font-normal">(optional)</span>
                                </label>
                                <input
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder="e.g. For Esri ArcGIS Pro connection"
                                    className="w-full bg-bg border border-bg-border rounded px-2.5 py-1.5
                                               text-sm text-white placeholder:text-gray-600
                                               focus:outline-none focus:border-accent-info/50"
                                />
                            </div>

                            {error && (
                                <div className="text-xs text-red-400 flex items-center gap-1.5">
                                    <AlertTriangle size={12} />
                                    {error}
                                </div>
                            )}
                        </>
                    ) : (
                        /* Key display — shown once */
                        <div className="space-y-3">
                            <div className="flex items-center gap-1.5 text-amber-400 text-xs">
                                <AlertTriangle size={13} />
                                Copy this key now — it cannot be retrieved again.
                            </div>
                            <div className="flex items-center gap-2 bg-bg rounded border border-bg-border px-3 py-2">
                                <code className="flex-1 text-xs font-mono text-teal-300 break-all">
                                    {newKey}
                                </code>
                                <button
                                    onClick={copyKey}
                                    className="shrink-0 text-gray-400 hover:text-white transition-colors"
                                >
                                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-500">
                                Scope: <span className="text-white">{scope.toUpperCase()}</span>
                                {note && <> · Note: <span className="text-white">{note}</span></>}
                            </p>
                        </div>
                    )}

                </div>

                <div className="px-5 py-3 border-t border-bg-border flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 rounded border border-bg-border text-xs text-gray-400 hover:text-gray-200"
                    >
                        {newKey ? "Done" : "Cancel"}
                    </button>
                    {!newKey && (
                        <button
                            onClick={generate}
                            disabled={saving || !name.trim()}
                            className="px-3 py-1.5 rounded border border-accent-action/40
                                       bg-accent-action/10 text-accent-action text-xs
                                       disabled:opacity-40 hover:bg-accent-action/20 transition-colors"
                        >
                            {saving ? "Generating…" : "Generate Key"}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Revoke Confirmation
// ---------------------------------------------------------------------------

function RevokeConfirm({ keyName, onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
            <div className="bg-bg-soft border border-bg-border rounded-lg w-full max-w-sm mx-4 shadow-xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-white">Revoke API Key?</p>
                        <p className="text-xs text-gray-400 mt-1">
                            <span className="text-white font-medium">{keyName}</span> will be
                            immediately invalidated. Any client using this key will lose access.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 rounded border border-bg-border text-xs text-gray-400 hover:text-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-1.5 rounded border border-red-500/40
                                   bg-red-500/10 text-red-400 text-xs
                                   hover:bg-red-500/20 transition-colors"
                    >
                        Revoke Key
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export default function OrgApiKeysPanel({ orgId }) {
    const [keys, setKeys]               = useState([]);
    const [loading, setLoading]         = useState(true);
    const [showGenerate, setShowGenerate] = useState(false);
    const [revoking, setRevoking]       = useState(null); // key object to revoke
    const [error, setError]             = useState(null);

    async function loadKeys() {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/api-keys`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setKeys(data.api_keys ?? []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    async function revokeKey(key) {
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/orgs/${orgId}/api-keys/${key.id}`,
                { method: "DELETE" }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await loadKeys();
        } catch (e) {
            alert(`Revoke failed: ${e.message}`);
        } finally {
            setRevoking(null);
        }
    }

    useEffect(() => { loadKeys(); }, [orgId]);

    const activeKeys  = keys.filter(k => !k.revoked_at);
    const revokedKeys = keys.filter(k => k.revoked_at);

    return (
        <div className="space-y-4">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xs font-semibold text-white font-mono uppercase tracking-wide">
                        API Keys
                    </h3>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                        WFS and WMS keys for external GIS client access.
                    </p>
                </div>
                <button
                    onClick={() => setShowGenerate(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border
                               border-accent-action/40 bg-accent-action/10 text-accent-action
                               text-xs hover:bg-accent-action/20 transition-colors"
                >
                    <Plus size={12} />
                    Generate
                </button>
            </div>

            {/* Loading */}
            {loading && (
                <div className="text-xs text-gray-500 animate-pulse">Loading keys…</div>
            )}

            {/* Error */}
            {error && (
                <div className="text-xs text-red-400 flex items-center gap-1.5">
                    <AlertTriangle size={12} /> {error}
                </div>
            )}

            {/* Active keys */}
            {!loading && activeKeys.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
                    <Key size={20} className="text-gray-600" />
                    <p className="text-xs text-gray-500">No active API keys.</p>
                    <p className="text-[10px] text-gray-600">
                        Generate a key to allow external GIS clients to access WFS or WMS.
                    </p>
                </div>
            )}

            {activeKeys.length > 0 && (
                <div className="space-y-1">
                    {activeKeys.map(k => (
                        <div key={k.id}
                             className="flex items-center gap-3 px-3 py-2.5 rounded
                                        bg-bg border border-bg-border hover:border-gray-600
                                        transition-colors group">

                            {/* Scope badge */}
                            {scopeBadge(k.scope)}

                            {/* Name + prefix */}
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-white truncate">
                                    {k.name}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] font-mono text-gray-500">
                                        {k.prefix}…
                                    </span>
                                    {k.note && (
                                        <span className="text-[10px] text-gray-600 truncate">
                                            {k.note}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Last used */}
                            <div className="text-[10px] text-gray-500 shrink-0 text-right">
                                {k.last_used_at ? (
                                    <>
                                        <div>last used</div>
                                        <div>{timeAgo(k.last_used_at)}</div>
                                    </>
                                ) : (
                                    <div className="text-gray-600">never used</div>
                                )}
                            </div>

                            {/* Created */}
                            <div className="text-[10px] text-gray-600 shrink-0 text-right hidden lg:block">
                                <div>created</div>
                                <div>{new Date(k.created_at).toLocaleDateString()}</div>
                            </div>

                            {/* Revoke button */}
                            <button
                                onClick={() => setRevoking(k)}
                                className="shrink-0 p-1 rounded text-gray-600
                                           hover:text-red-400 hover:bg-red-500/10
                                           transition-colors opacity-0 group-hover:opacity-100"
                                title="Revoke key"
                            >
                                <Trash2 size={13} />
                            </button>

                        </div>
                    ))}
                </div>
            )}

            {/* Revoked keys — collapsed section */}
            {revokedKeys.length > 0 && (
                <details className="group">
                    <summary className="text-[10px] text-gray-600 cursor-pointer
                                        hover:text-gray-400 transition-colors select-none">
                        {revokedKeys.length} revoked key{revokedKeys.length !== 1 ? "s" : ""}
                    </summary>
                    <div className="mt-2 space-y-1">
                        {revokedKeys.map(k => (
                            <div key={k.id}
                                 className="flex items-center gap-3 px-3 py-2 rounded
                                            bg-bg border border-bg-border opacity-50">
                                {scopeBadge(k.scope)}
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-gray-500 line-through truncate">
                                        {k.name}
                                    </div>
                                    <div className="text-[10px] font-mono text-gray-600">
                                        {k.prefix}…
                                    </div>
                                </div>
                                <div className="text-[10px] text-red-500/60 shrink-0">
                                    revoked {timeAgo(k.revoked_at)}
                                </div>
                            </div>
                        ))}
                    </div>
                </details>
            )}

            {/* Modals */}
            {showGenerate && (
                <GenerateKeyModal
                    orgId={orgId}
                    onCreated={loadKeys}
                    onClose={() => setShowGenerate(false)}
                />
            )}

            {revoking && (
                <RevokeConfirm
                    keyName={revoking.name}
                    onConfirm={() => revokeKey(revoking)}
                    onCancel={() => setRevoking(null)}
                />
            )}

        </div>
    );
}
